import { t } from "@hakili/i18n";
import { useEffect, useState } from "react";

/**
 * `P1-IDN-047`: **the invitation's own face**, at `/invitations/{token}` on the
 * partner hosts. WF-CON-29's visible half.
 *
 * The five-minute onboarding has had a back half since `P0-IDN-005` , the token,
 * the acceptance route, the account turning `actif` , and no front. The banker
 * sitting of 30 August walked to step 3c and stopped at a URL nothing rendered.
 *
 * **It names who is being invited before it asks for anything.** A password
 * field on a page that has not said which organisation it belongs to is a
 * phishing form with a real domain; the invitation's own answer carries the
 * organisation, the role and the name, and this screen shows all three first.
 *
 * **A real `<form>`**, so `Enter` submits and a password manager offers to fill
 * it , round 185 paid for that lesson on the client app, with Chrome naming the
 * defect out loud.
 *
 * **The confirmation field is compared HERE and the refusal is the server's
 * everywhere else.** Two typed passwords that differ is the one refusal this
 * screen can know without asking; everything else , a spent token, an expired
 * one, a password the policy refuses , is the server's sentence, rendered as
 * given.
 */

export interface InvitationVue {
  readonly email: string;
  readonly fullName: string;
  readonly role: string;
  readonly organisationName: string;
}

const asString = (value: unknown): string | null => (typeof value === "string" ? value : null);

/** The invitation, or `null` when the answer is not the shape this screen renders. */
export function invitationFrom(answer: unknown): InvitationVue | null {
  if (typeof answer !== "object" || answer === null) return null;
  const raw = answer as Record<string, unknown>;
  const email = asString(raw["email"]);
  const fullName = asString(raw["fullName"]);
  const role = asString(raw["role"]);
  const organisationName = asString(raw["organisationName"]);
  if (email === null || fullName === null || role === null || organisationName === null) return null;
  return { email, fullName, role, organisationName };
}

export function Invitation(props: {
  readonly token: string;
  readonly lire: (token: string) => Promise<unknown>;
  readonly accepter: (token: string, password: string) => Promise<void>;
}): React.JSX.Element {
  const [vue, setVue] = useState<InvitationVue | null>(null);
  const [introuvable, setIntrouvable] = useState(false);
  const [motDePasse, setMotDePasse] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [refus, setRefus] = useState("");
  const [enCours, setEnCours] = useState(false);
  const [reussite, setReussite] = useState(false);

  useEffect(() => {
    let live = true;
    void props
      .lire(props.token)
      .then((answer) => {
        if (!live) return;
        const lu = invitationFrom(answer);
        setVue(lu);
        setIntrouvable(lu === null);
      })
      .catch(() => {
        // A spent token, an expired one and a transport failure are one answer
        // on this screen: there is nothing here to join. The server's own
        // sentence appears on the ACCEPT attempt, where it can still be acted on.
        if (live) setIntrouvable(true);
      });
    return () => {
      live = false;
    };
  }, [props]);

  if (reussite) {
    return (
      <main className="mx-auto flex min-h-screen max-w-sm flex-col justify-center gap-4 px-6">
        <p role="status" data-testid="invitation-reussite">
          {t("bank.invitation.reussite")}
        </p>
      </main>
    );
  }

  if (introuvable) {
    return (
      <main className="mx-auto flex min-h-screen max-w-sm flex-col justify-center gap-4 px-6">
        <p role="alert" data-testid="invitation-introuvable">
          {t("bank.invitation.introuvable")}
        </p>
      </main>
    );
  }

  if (vue === null) {
    return <p className="p-6">{t("bank.session.chargement")}</p>;
  }

  const soumettre = (event: Pick<Event, "preventDefault">): void => {
    event.preventDefault();
    if (motDePasse !== confirmation) {
      setRefus(t("bank.invitation.differents"));
      return;
    }
    setRefus("");
    setEnCours(true);
    void props
      .accepter(props.token, motDePasse)
      .then(() => {
        setReussite(true);
      })
      .catch((cause: unknown) => {
        // The SERVER's sentence, rendered as given. A client that wrote its own
        // would be guessing which rule refused.
        setRefus(cause instanceof Error ? cause.message : t("bank.invitation.introuvable"));
      })
      .finally(() => {
        setEnCours(false);
      });
  };

  return (
    <main className="mx-auto flex min-h-screen max-w-sm flex-col justify-center gap-6 px-6">
      <h1 className="text-2xl font-semibold">{t("bank.invitation.titre")}</h1>
      <div className="text-sm text-gray-600" data-testid="invitation-qui">
        <p>
          {t("bank.invitation.bienvenue")}, <strong>{vue.fullName}</strong>
        </p>
        <p>
          {t("bank.invitation.organisation")} : <strong>{vue.organisationName}</strong>
        </p>
        <p>
          {t("bank.invitation.role")} : <strong>{vue.role}</strong>
        </p>
      </div>

      <form className="flex flex-col gap-4" onSubmit={soumettre} data-testid="invitation-form">
        <label className="flex flex-col gap-1 text-sm">
          {t("bank.invitation.motDePasse")}
          <input
            type="password"
            name="new-password"
            autoComplete="new-password"
            required
            value={motDePasse}
            onChange={(event) => {
              setMotDePasse(event.target.value);
            }}
            className="rounded border px-3 py-2"
            data-testid="invitation-mot-de-passe"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          {t("bank.invitation.confirmer")}
          <input
            type="password"
            name="confirm-password"
            autoComplete="new-password"
            required
            value={confirmation}
            onChange={(event) => {
              setConfirmation(event.target.value);
            }}
            className="rounded border px-3 py-2"
            data-testid="invitation-confirmation"
          />
        </label>
        {refus === "" ? null : (
          <p role="alert" className="text-sm text-rose-600" data-testid="invitation-refus">
            {refus}
          </p>
        )}
        <button
          type="submit"
          disabled={enCours}
          className="rounded bg-indigo-600 px-3 py-2 text-white disabled:opacity-60"
          data-testid="invitation-accepter"
        >
          {enCours ? t("bank.invitation.encours") : t("bank.invitation.accepter")}
        </button>
      </form>
    </main>
  );
}
